import type { Controls } from 'three'
import type { EngineContext } from '@/runtime/EngineContext'
import type { PhaseRenderContext, RenderPhase } from '@/shared/types/assets'
import type { ControlMode, Disposable, ViewportRect } from '@/shared/types/core'
import type { Plugin } from '@/shared/types/plugin'
import type {
  CameraRig,
  ControlManager,
  ControlModeFactory,
  ControlModeRegistry,
  PickingService,
  RenderActivityContext,
  RenderActivityReport,
  RenderContributor,
  RenderInvalidationService,
  RenderPipeline,
  SpatialQueryService,
  ViewportCamera,
  ViewRegistry,
} from '@/shared/types/services'
import type { EngineFrameContext, ViewFrameContext } from '@/shared/types/view'
import { ActiveViewControlManagerFacade } from './ActiveViewControlManagerFacade'
import {
  CameraFreeLookController,
  type CameraFreeLookControllerSettings,
} from './CameraFreeLookController'
import { ControlManagerImpl } from './ControlManager'
import { ControlModeRegistryImpl } from './ControlModeRegistry'
import { CONTROL_GRAPHICS_REQUIREMENTS } from './ControlOverlayGraphicsAdapter'
import { createViewScopedDomElement } from './createViewScopedDomElement'
import { EarthController } from './EarthController'
import { FirstPersonController } from './FirstPersonController'
import { FlyController } from './FlyController'
import { LegacyFirstPersonController } from './LegacyFirstPersonController'
import { MapController } from './MapController'
import {
  DEFAULT_NATIVE_CONTROL_MIN_ZOOM_STEP,
  DEFAULT_NATIVE_CONTROL_SURFACE_PICK_NEAR_OFFSET,
  DEFAULT_NATIVE_CONTROL_SURFACE_PICK_WINDOW_SIZE,
  type NativeControlSurfacePickingOptions,
  resolveNativeControlMinZoomStep,
  resolveNativeControlPickWindowSize,
} from './NativeControlSurfacePickingOptions'
import {
  createViewLocalPickingService,
  createViewLocalSurfaceQueryService,
  type NativeControlServices,
} from './NativeControlUtils'
import { OrbitController, type OrbitControllerSettings } from './OrbitController'
import {
  SpaceMouseController,
  type SpaceMouseNavigationProfile,
  type SpaceMousePresenceProvider,
} from './SpaceMouseController'

/** ControlPlugin 构造参数 */
export interface ControlPluginOptions {
  /**
   * 预先构造好的控制器数组。
   *
   * @deprecated 仅保证注入到 primary view，不会复用到其他 view。
   */
  extraControls?: ReadonlyArray<readonly [ControlMode, Controls<any>]>
  /** 控制器工厂数组，多视口场景下每个视图独立构造 */
  extraControlFactories?: ReadonlyArray<
    readonly [ControlMode, ControlModeFactory<NativeControlServices>]
  >
  /** 默认启用的控制模式，默认 'orbit' */
  defaultMode?: ControlMode
  /** camera-free-look 内置控制器参数 */
  cameraFreeLook?: CameraFreeLookControllerSettings
  /** orbit 内置控制器参数 */
  orbit?: OrbitControllerSettings
  /** Earth 与 SpaceMouse 的表面拾取近平面偏移配置，nearOffset 默认 0.1 */
  surfacePicking?: NativeControlSurfacePickingOptions
  /** SpaceMouse 表面拾取窗口像素尺寸，默认 129。 */
  spaceMouseWindowSize?: number
  /** SpaceMouse 缩放绝对最小步长，单位为世界坐标，默认 0.3。 */
  spaceMouseMinZoomStep?: number
  /** EarthController 表面拾取窗口像素尺寸，默认 129。 */
  earthWindowSize?: number
  /** EarthController 缩放绝对最小步长，单位为世界坐标，默认 0.3。 */
  earthMinZoomStep?: number
  spaceMouseNavigationProfile?: SpaceMouseNavigationProfile
  spaceMousePresenceProvider?: SpaceMousePresenceProvider
  spaceMousePresencePollIntervalMs?: number
}

interface ControlViewResource {
  manager: ControlManagerImpl
  contributors: RenderContributor[]
  inputActivity: ControlInputActivityContributor
  services: NativeControlServices
}

/**
 * Keep keyboard-driven controllers alive in on-demand mode.
 *
 * Most legacy controls own their own DOM listeners, so a key event does not
 * pass through InputRouter's consumer invalidation path. This contributor
 * bridges that input state to RenderPipeline without changing continuous-mode
 * behavior or the controller implementations themselves.
 */
class ControlInputActivityContributor implements RenderContributor {
  readonly kind = 'control-input-activity'
  readonly phases: readonly RenderPhase[] = []
  readonly graphicsRequirements = CONTROL_GRAPHICS_REQUIREMENTS
  private readonly pressedKeys = new Set<string>()

  constructor(private readonly viewId: string) {}

  render(_frame: ViewFrameContext, _phase: RenderPhase, _renderContext: PhaseRenderContext): void {}

  setKey(code: string, pressed: boolean): void {
    if (!code) return
    if (pressed) {
      this.pressedKeys.add(code)
    } else {
      this.pressedKeys.delete(code)
    }
  }

  clear(): void {
    this.pressedKeys.clear()
  }

  getRenderActivity(context: RenderActivityContext): RenderActivityReport {
    if (
      this.pressedKeys.size === 0 ||
      context.activeViewId !== this.viewId ||
      !context.renderedViewIds.includes(this.viewId)
    ) {
      return { needsNextFrame: false, reasons: [] }
    }
    return {
      needsNextFrame: true,
      reasons: ['control.keyboard'],
      viewIds: [this.viewId],
    }
  }
}

/** Registers and manages the engine's camera controls. */
export class ControlPlugin implements Plugin {
  /** 插件名称 */
  readonly name = 'control'
  readonly graphicsRequirements = CONTROL_GRAPHICS_REQUIREMENTS
  /** 插件启动优先级 */
  readonly priority = 15
  /** 依赖的服务名列表 */
  readonly dependencies = [
    'ViewRegistry',
    'CameraRig',
    'ViewportCamera',
    'PickingService',
    'RenderPipeline',
  ] as const
  /** 对外提供的服务名列表 */
  readonly provides = ['ControlManager', 'ControlModeRegistry'] as const

  private manager: ControlManager | null = null
  private controlModeRegistry: ControlModeRegistry | null = null
  private context: EngineContext | null = null
  private viewRegistry: ViewRegistry | null = null
  private renderPipeline: RenderPipeline | null = null
  private pickingService: PickingService | null = null
  private spatialQueryService: SpatialQueryService | null = null
  private renderActivityDisposable: Disposable | null = null
  private readonly extraControls: ReadonlyArray<readonly [ControlMode, Controls<any>]>
  private readonly staticControlFactories = new Map<
    ControlMode,
    ControlModeFactory<NativeControlServices>
  >()
  private readonly runtimeControlFactories = new Map<
    ControlMode,
    ControlModeFactory<NativeControlServices>
  >()
  private readonly defaultMode: ControlMode
  private readonly cameraFreeLookSettings: CameraFreeLookControllerSettings
  private readonly orbitSettings: OrbitControllerSettings
  private readonly surfacePicking: NativeControlSurfacePickingOptions
  private readonly spaceMouseWindowSize: number
  private readonly spaceMouseMinZoomStep: number
  private readonly earthWindowSize: number
  private readonly earthMinZoomStep: number
  private readonly defaultSpaceMouseNavigationProfile: SpaceMouseNavigationProfile
  private readonly spaceMousePresenceProvider: SpaceMousePresenceProvider | null
  private readonly spaceMousePresencePollIntervalMs: number | undefined
  private readonly resources = new Map<string, ControlViewResource>()
  private readonly handleWindowKeyDown = (event: KeyboardEvent) => {
    this.handleWindowKey(event, true)
  }
  private readonly handleWindowKeyUp = (event: KeyboardEvent) => {
    this.handleWindowKey(event, false)
  }
  private readonly handleWindowBlur = () => {
    this.clearKeyboardActivity()
  }
  private readonly builtInModes = new Set<ControlMode>([
    'orbit',
    'spacemouse',
    'first-person',
    'legacy-first-person',
    'camera-free-look',
    'fly',
    'map',
    'earth',
  ])
  private disposed = false
  private readonly handleViewCreated = (event: { viewId: string }) => {
    const resource = this.ensureViewResource(event.viewId)
    this.attachContributors(resource.contributors)
  }
  private readonly handleViewRemoved = (event: { viewId: string }) => {
    const resource = this.resources.get(event.viewId)
    if (!resource) {
      return
    }
    this.detachContributors(resource.contributors)
    resource.manager.dispose()
    this.resources.delete(event.viewId)
  }
  private readonly handleViewActivated = (event: {
    viewId: string
    previousViewId: string | null
  }) => {
    this.clearKeyboardActivity()
    const next = this.ensureViewResource(event.viewId)
    const previous = event.previousViewId
      ? (this.resources.get(event.previousViewId) ?? null)
      : null

    previous?.manager.setInputActive(false)
    next.manager.setInputActive(true)

    if (!previous || previous.manager.activeMode !== next.manager.activeMode) {
      this.context?.events.emit('controls.modeChanged', {
        mode: next.manager.activeMode,
        previous: previous?.manager.activeMode ?? next.manager.activeMode,
      })
    }
  }

  constructor(options?: ControlPluginOptions) {
    this.extraControls = options?.extraControls ?? []
    for (const [mode, factory] of options?.extraControlFactories ?? []) {
      this.registerStaticFactory(mode, factory)
    }
    this.defaultMode = options?.defaultMode ?? 'orbit'
    this.cameraFreeLookSettings = options?.cameraFreeLook ?? {}
    this.orbitSettings = options?.orbit ?? {}
    this.surfacePicking = resolveSurfacePickingOptions(options?.surfacePicking)
    this.spaceMouseWindowSize = resolveNativeControlPickWindowSize(
      options?.spaceMouseWindowSize,
      'spaceMouseWindowSize'
    )
    this.spaceMouseMinZoomStep = resolveNativeControlMinZoomStep(
      options?.spaceMouseMinZoomStep,
      'spaceMouseMinZoomStep'
    )
    this.earthWindowSize = resolveNativeControlPickWindowSize(
      options?.earthWindowSize,
      'earthWindowSize'
    )
    this.earthMinZoomStep = resolveNativeControlMinZoomStep(
      options?.earthMinZoomStep,
      'earthMinZoomStep'
    )
    this.defaultSpaceMouseNavigationProfile = options?.spaceMouseNavigationProfile ?? 'hybrid'
    this.spaceMousePresenceProvider = options?.spaceMousePresenceProvider ?? null
    this.spaceMousePresencePollIntervalMs = options?.spaceMousePresencePollIntervalMs
  }

  /**
   * 初始化插件，注册 ControlManager。
   *
   * @param context - 引擎上下文
   */
  onInit(context: EngineContext): void {
    this.context = context
    this.manager = new ActiveViewControlManagerFacade(() => this.getActiveManager())
    this.controlModeRegistry = new ControlModeRegistryImpl({
      registerFactory: (mode, factory) => this.registerRuntimeFactory(mode, factory),
      unregisterFactory: (mode) => this.unregisterRuntimeFactory(mode),
      has: (mode) => this.hasRegisteredMode(mode),
      listModes: () => this.listRegisteredModes(),
    })
    context.registerService('ControlManager', this.manager)
    context.registerService('ControlModeRegistry', this.controlModeRegistry)
  }

  /** 启动插件，为每个视图创建控制器并订阅视图事件 */
  onStart(): void {
    if (!this.context || !this.manager) {
      throw new Error('ControlPlugin is not initialized')
    }

    const container = this.context.container
    if (!container.hasAttribute('tabindex')) {
      container.setAttribute('tabindex', '0')
    }

    this.viewRegistry = this.context.getService('ViewRegistry')
    this.renderPipeline = this.context.getService('RenderPipeline')
    this.pickingService = this.context.getService('PickingService')
    this.spatialQueryService = this.context.hasService('SpatialQueryService')
      ? this.context.getService('SpatialQueryService')
      : null

    for (const view of this.viewRegistry.list()) {
      const resource = this.ensureViewResource(view.id)
      this.attachContributors(resource.contributors)
    }

    this.context.events.on('view.created', this.handleViewCreated)
    this.context.events.on('view.removed', this.handleViewRemoved)
    this.context.events.on('view.activated', this.handleViewActivated)

    const activeViewId = this.viewRegistry.getActive()?.id
    if (activeViewId) {
      this.ensureViewResource(activeViewId).manager.setInputActive(true)
    }

    if (this.context.hasService('RenderInvalidationService')) {
      const registeredInvalidation = this.context.getService('RenderInvalidationService') as
        | Partial<RenderInvalidationService>
        | undefined
      const renderInvalidation =
        registeredInvalidation?.registerActivitySource !== undefined
          ? (registeredInvalidation as RenderInvalidationService)
          : this.context.renderInvalidation
      if (renderInvalidation?.registerActivitySource) {
        this.renderActivityDisposable = renderInvalidation.registerActivitySource({
          id: 'ControlPlugin',
          getRenderActivity: () => {
            const controller = this.getActiveManager().activeController
            const needsNextFrame =
              controller instanceof OrbitController && controller.hasPendingElasticAnimation()
            return needsNextFrame
              ? { needsNextFrame: true, reasons: ['controls.orbitElastic'] }
              : { needsNextFrame: false }
          },
        })
      }
      window.addEventListener('keydown', this.handleWindowKeyDown, true)
      window.addEventListener('keyup', this.handleWindowKeyUp, true)
      window.addEventListener('blur', this.handleWindowBlur)
    }
  }

  /**
   * 每帧更新活动控制器。
   *
   * @param frame - 引擎帧上下文
   */
  onUpdateFrame(frame: EngineFrameContext): void {
    this.getActiveManager().update(frame)
  }

  /** 销毁插件，释放所有视图控制器与订阅 */
  onDestroy(): void {
    if (this.disposed) return
    this.disposed = true

    this.renderActivityDisposable?.dispose()
    this.renderActivityDisposable = null

    this.context?.events.off('view.created', this.handleViewCreated)
    this.context?.events.off('view.removed', this.handleViewRemoved)
    this.context?.events.off('view.activated', this.handleViewActivated)
    window.removeEventListener('keydown', this.handleWindowKeyDown, true)
    window.removeEventListener('keyup', this.handleWindowKeyUp, true)
    window.removeEventListener('blur', this.handleWindowBlur)
    this.clearKeyboardActivity()

    for (const resource of this.resources.values()) {
      this.detachContributors(resource.contributors)
    }

    for (const resource of this.resources.values()) {
      resource.manager.dispose()
    }
    this.resources.clear()

    this.manager?.dispose()
    this.manager = null
    this.controlModeRegistry?.dispose()
    this.controlModeRegistry = null
    this.renderPipeline = null
    this.pickingService = null
    this.spatialQueryService = null
    this.viewRegistry = null
    this.runtimeControlFactories.clear()
    this.context = null
  }

  private ensureViewResource(viewId: string): ControlViewResource {
    const existing = this.resources.get(viewId)
    if (existing) {
      return existing
    }

    if (!this.context || !this.viewRegistry || !this.pickingService) {
      throw new Error('ControlPlugin 依赖尚未完成初始化')
    }

    const rig = this.viewRegistry.getExtension(viewId, 'cameraRig') as CameraRig | undefined
    const viewportCamera = this.viewRegistry.getExtension(viewId, 'viewportCamera') as
      | ViewportCamera
      | undefined
    if (!rig || !viewportCamera) {
      throw new Error(`View "${viewId}" 缺少相机资源，无法创建控制器`)
    }

    const interactionElement = createViewScopedDomElement(this.context.container, () =>
      this.getViewRect(viewId)
    )
    const manager = new ControlManagerImpl(this.context.events, interactionElement, {
      viewId,
      inputActive: this.viewRegistry.getActive()?.id === viewId,
    })
    const services = this.createNativeServices(viewId, rig, viewportCamera)

    manager.register('orbit', new OrbitController({ services, ...this.orbitSettings }))
    const spaceMouseController = new SpaceMouseController({
      services,
      surfacePicking: this.surfacePicking,
      windowSize: this.spaceMouseWindowSize,
      minZoomStep: this.spaceMouseMinZoomStep,
      navigationProfile: this.defaultSpaceMouseNavigationProfile,
      presenceProvider: this.spaceMousePresenceProvider ?? undefined,
      presencePollIntervalMs: this.spaceMousePresencePollIntervalMs,
    })
    manager.register('spacemouse', spaceMouseController)
    manager.register('first-person', new FirstPersonController({ services }))
    manager.register(
      'camera-free-look',
      new CameraFreeLookController({ services, ...this.cameraFreeLookSettings })
    )
    manager.register('legacy-first-person', new LegacyFirstPersonController({ services }))
    manager.register('fly', new FlyController({ services }))
    manager.register('map', new MapController({ services }))
    const earthController = new EarthController({
      services,
      surfacePicking: this.surfacePicking,
      windowSize: this.earthWindowSize,
      minZoomStep: this.earthMinZoomStep,
    })
    manager.register('earth', earthController)

    this.registerFactories(manager, services, this.staticControlFactories)
    this.registerFactories(manager, services, this.runtimeControlFactories)
    if (viewId === this.viewRegistry.primaryViewId) {
      for (const [mode, control] of this.extraControls) {
        manager.register(mode, control)
      }
    }

    manager.setMode(this.defaultMode)

    const resource: ControlViewResource = {
      manager,
      inputActivity: new ControlInputActivityContributor(viewId),
      contributors: [earthController, spaceMouseController],
      services,
    }
    resource.contributors.push(resource.inputActivity)
    this.resources.set(viewId, resource)
    this.viewRegistry.setExtension(viewId, 'controlManager', manager)
    return resource
  }

  private handleWindowKey(event: KeyboardEvent, pressed: boolean): void {
    if (!this.context || !this.viewRegistry) return
    const target = event.target as HTMLElement | null
    const tagName = target?.tagName?.toUpperCase()
    if (tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT') {
      return
    }

    const activeView = this.viewRegistry.getActive()
    if (!activeView) return
    const resource = this.resources.get(activeView.id)
    if (!resource || resource.manager.activeMode === 'none') return

    resource.inputActivity.setKey(event.code, pressed)
    this.context.renderInvalidation?.invalidate(
      pressed ? 'input.windowKeydown' : 'input.windowKeyup',
      { viewId: activeView.id }
    )
  }

  private clearKeyboardActivity(): void {
    for (const resource of this.resources.values()) {
      resource.inputActivity.clear()
    }
  }

  private createNativeServices(
    viewId: string,
    rig: CameraRig,
    viewportCamera: ViewportCamera
  ): NativeControlServices {
    const rendererDomElement = createViewScopedDomElement(this.context!.graphics.canvas, () =>
      this.getViewRect(viewId)
    )
    const getViewClientRect = (): ViewportRect => {
      const rect = rendererDomElement.getBoundingClientRect()
      return {
        x: rect.left,
        y: rect.top,
        width: rect.width,
        height: rect.height,
      }
    }
    const viewLocalPicking = this.pickingService
      ? createViewLocalPickingService(
          viewId,
          () => this.getViewRect(viewId),
          getViewClientRect,
          this.pickingService
        )
      : undefined

    return {
      viewId,
      preferAsyncPicking: this.context!.graphics.kind === 'webgpu',
      rig,
      viewportCamera,
      events: this.context!.events,
      rendererDomElement,
      getViewportSize: () => {
        const rect = this.getViewRect(viewId)
        return { width: rect.width, height: rect.height }
      },
      getViewRect: () => this.getViewRect(viewId),
      picking: viewLocalPicking,
      surfaceQuery: this.pickingService
        ? createViewLocalSurfaceQueryService(
            viewId,
            () => this.getViewRect(viewId),
            getViewClientRect,
            this.pickingService,
            this.spatialQueryService ?? undefined
          )
        : undefined,
      spatial: this.spatialQueryService ?? undefined,
      getSceneBounds: (purpose = 'default') =>
        this.context!.sceneBounds.getWorldBoundingBox({ purpose }),
    }
  }

  private getViewRect(viewId: string): ViewportRect {
    const rect = this.viewRegistry?.get(viewId)?.rect
    if (rect) {
      return rect
    }
    const size = this.context!.getSize()
    return { x: 0, y: 0, width: size.width, height: size.height }
  }

  private getActiveManager(): ControlManagerImpl {
    const activeViewId = this.viewRegistry?.getActive()?.id
    if (activeViewId) {
      return this.ensureViewResource(activeViewId).manager
    }
    const first = this.resources.values().next().value as ControlViewResource | undefined
    if (first) {
      return first.manager
    }
    throw new Error('当前没有可用的 ControlManager')
  }

  private attachContributors(contributors: RenderContributor[]): void {
    if (!this.renderPipeline) {
      return
    }
    for (const contributor of contributors) {
      this.renderPipeline.addRenderContributor(contributor)
    }
  }

  private detachContributors(contributors: RenderContributor[]): void {
    if (!this.renderPipeline) {
      return
    }
    for (const contributor of contributors) {
      this.renderPipeline.removeRenderContributor(contributor)
    }
  }

  private registerFactories(
    manager: ControlManagerImpl,
    services: NativeControlServices,
    factories: ReadonlyMap<ControlMode, ControlModeFactory<NativeControlServices>>
  ): void {
    for (const [mode, factory] of factories) {
      manager.register(mode, factory(services))
    }
  }

  private registerStaticFactory(
    mode: ControlMode,
    factory: ControlModeFactory<NativeControlServices>
  ): void {
    this.assertFactoryModeAvailable(mode, this.staticControlFactories)
    this.staticControlFactories.set(mode, factory)
  }

  private registerRuntimeFactory(
    mode: ControlMode,
    factory: ControlModeFactory<NativeControlServices>
  ): void {
    this.assertFactoryModeAvailable(mode, this.runtimeControlFactories)
    this.runtimeControlFactories.set(mode, factory)

    for (const resource of this.resources.values()) {
      resource.manager.register(mode, factory(resource.services))
    }
  }

  private unregisterRuntimeFactory(mode: ControlMode): boolean {
    if (!this.runtimeControlFactories.delete(mode)) {
      return false
    }

    for (const resource of this.resources.values()) {
      if (resource.manager.activeMode === mode) {
        const fallbackMode =
          this.defaultMode !== mode && resource.manager.getController(this.defaultMode)
            ? this.defaultMode
            : 'none'
        resource.manager.setMode(fallbackMode)
      }
      resource.manager.unregister(mode)
    }

    return true
  }

  private hasRegisteredMode(mode: ControlMode): boolean {
    return (
      this.builtInModes.has(mode) ||
      this.staticControlFactories.has(mode) ||
      this.runtimeControlFactories.has(mode)
    )
  }

  private listRegisteredModes(): ControlMode[] {
    return [
      ...this.builtInModes,
      ...this.staticControlFactories.keys(),
      ...this.runtimeControlFactories.keys(),
    ]
  }

  private assertFactoryModeAvailable(
    mode: ControlMode,
    target: ReadonlyMap<ControlMode, ControlModeFactory<NativeControlServices>>
  ): void {
    if (mode === 'none') {
      throw new Error('ControlPlugin: "none" is reserved and cannot be registered')
    }

    if (this.builtInModes.has(mode)) {
      throw new Error(`ControlPlugin: "${mode}" is a built-in control mode`)
    }

    if (this.extraControls.some(([registeredMode]) => registeredMode === mode)) {
      throw new Error(`ControlPlugin: "${mode}" is already reserved by extraControls`)
    }

    if (
      target.has(mode) ||
      this.staticControlFactories.has(mode) ||
      this.runtimeControlFactories.has(mode)
    ) {
      throw new Error(`ControlPlugin: "${mode}" is already registered`)
    }
  }
}

export type { NativeControlSurfacePickingOptions } from './NativeControlSurfacePickingOptions'
export {
  DEFAULT_NATIVE_CONTROL_MIN_ZOOM_STEP,
  DEFAULT_NATIVE_CONTROL_SURFACE_PICK_NEAR_OFFSET,
  DEFAULT_NATIVE_CONTROL_SURFACE_PICK_WINDOW_SIZE,
  resolveNativeControlMinZoomStep,
  resolveNativeControlPickWindowSize,
}

function resolveSurfacePickingOptions(
  options: NativeControlSurfacePickingOptions | undefined
): NativeControlSurfacePickingOptions {
  const nearOffset = options?.nearOffset
  if (nearOffset !== undefined && (!Number.isFinite(nearOffset) || nearOffset < 0)) {
    throw new RangeError('surfacePicking.nearOffset 必须是有限且不小于零的数')
  }
  return Object.freeze({
    nearOffset: nearOffset ?? DEFAULT_NATIVE_CONTROL_SURFACE_PICK_NEAR_OFFSET,
  })
}
